import React from 'react';
import { Helmet } from 'react-helmet-async';
import { companyInfo } from '../data/mock';

export const SITE_URL = 'https://www.happyvan.co';

// Default social share card. Swap per page by passing `ogImage` (1200x630 recommended).
// TODO(happyvan): replace /og-image.png with a real van/team photo at 1200x630.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = 'HappyVan removals, Brighton & Hove',
  ogImageWidth = 1200,
  ogImageHeight = 630,
  type = 'website',
  noindex = false,
  schema = null,
  faqSchema = null,
  breadcrumbs = null
}) => {
  const schemas = [];
  if (Array.isArray(schema)) schemas.push(...schema.filter(Boolean));
  else if (schema) schemas.push(schema);
  if (faqSchema) schemas.push(faqSchema);
  if (breadcrumbs && breadcrumbs.length) schemas.push(generateBreadcrumbSchema(breadcrumbs));

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="HappyVan" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content={String(ogImageWidth)} />
      <meta property="og:image:height" content={String(ogImageHeight)} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};

export const generateFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer }
  }))
});

export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`
  }))
});

export const generateServiceSchema = ({ name, description, areaServed, url }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: name,
  name,
  description,
  url: url ? `${SITE_URL}${url}` : SITE_URL,
  provider: { '@id': `${SITE_URL}/#business` },
  areaServed: (Array.isArray(areaServed) ? areaServed : [areaServed || 'Brighton and Hove']).map((a) => ({
    '@type': 'City',
    name: a
  })),
  availableChannel: {
    '@type': 'ServiceChannel',
    servicePhone: { '@type': 'ContactPoint', telephone: companyInfo.phoneE164, contactType: 'sales' },
    serviceUrl: `${SITE_URL}/get-quote`
  }
});

export const generateArticleSchema = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: post.title,
  description: post.metaDescription,
  image: post.image,
  datePublished: post.date,
  dateModified: post.updated || post.date,
  author: { '@type': 'Organization', name: companyInfo.name, url: SITE_URL },
  publisher: { '@id': `${SITE_URL}/#business` },
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${post.slug}` }
});

// Towns / areas with a dedicated page - keep in sync with locations.js + sitemap.xml
const areaServedNames = [
  'Brighton', 'Hove', 'Worthing', 'Shoreham-by-Sea', 'Lewes', 'Burgess Hill',
  'Eastbourne', 'Portslade', 'Southwick', 'Hassocks', 'Haywards Heath'
];

const openingHours = () => {
  const spec = [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: companyInfo.hours.weekday.opens,
      closes: companyInfo.hours.weekday.closes
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday'],
      opens: companyInfo.hours.saturday.opens,
      closes: companyInfo.hours.saturday.closes
    }
  ];
  // Sunday only published once confirmed (see companyInfo.hours.sunday TODO)
  if (companyInfo.hours.sunday === 'closed') {
    spec.push({ '@type': 'OpeningHoursSpecification', dayOfWeek: ['Sunday'], opens: '00:00', closes: '00:00' });
  } else if (companyInfo.hours.sunday && companyInfo.hours.sunday.opens) {
    spec.push({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Sunday'],
      opens: companyInfo.hours.sunday.opens,
      closes: companyInfo.hours.sunday.closes
    });
  }
  return spec;
};

// Single merged MovingCompany block - the only LocalBusiness schema on the site.
// No aggregateRating: it must come from verified review data, never hardcoded.
export const businessSchema = {
  '@context': 'https://schema.org',
  '@type': 'MovingCompany',
  '@id': `${SITE_URL}/#business`,
  name: companyInfo.name,
  legalName: companyInfo.legalName,
  url: SITE_URL,
  logo: `${SITE_URL}/happyvan-logo.png`,
  image: DEFAULT_OG_IMAGE,
  description:
    'Professional furniture removals and delivery in Brighton, Hove and across Sussex. House and office removals, man and van, collections, deliveries, packing materials and storage.',
  telephone: companyInfo.phoneE164,
  email: companyInfo.email,
  address: {
    '@type': 'PostalAddress',
    ...(companyInfo.streetAddress ? { streetAddress: companyInfo.streetAddress } : {}),
    addressLocality: companyInfo.addressLocality,
    addressRegion: companyInfo.addressRegion,
    postalCode: companyInfo.postalCode,
    addressCountry: companyInfo.addressCountry
  },
  geo: { '@type': 'GeoCoordinates', latitude: 50.8351, longitude: -0.1758 },
  areaServed: areaServedNames.map((name) => ({ '@type': 'City', name })),
  serviceType: [
    'House Removals',
    'Office Removals',
    'Man and Van',
    'Furniture Delivery',
    'Piano Removals',
    'Student Removals',
    'Packing Materials',
    'Storage Solutions'
  ],
  priceRange: '££',
  currenciesAccepted: 'GBP',
  openingHoursSpecification: openingHours(),
  sameAs: [
    companyInfo.social.facebook,
    companyInfo.social.instagram,
    companyInfo.social.googleBusinessProfile
  ].filter(Boolean),
  ...(companyInfo.foundingYear ? { foundingDate: String(companyInfo.foundingYear) } : {}),
  knowsAbout: [
    'furniture removals',
    'house moves Brighton',
    'man and van Brighton',
    'office relocations',
    'packing services',
    'Sussex removals'
  ]
};

// Legacy export name kept so existing pages keep working
export const homepageSchema = businessSchema;

export const serviceSchema = generateServiceSchema({
  name: 'House Removals',
  description:
    'Professional house removals in Brighton and Hove. Fully insured, experienced team with purpose-built Luton vans.',
  areaServed: ['Brighton', 'Hove'],
  url: '/house-removals-brighton'
});

export default SEOHead;
